import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { ClientSiblingPayload } from '../../../../../common';

export default function useTreatmentPlanV3ClientSiblings() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const siblings = useSelector(
    (state: RootState) => state.treatmentPlanV3.familyPage?.client_siblings ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.treatmentPlanV3.createClientSiblingLoading ||
      state.treatmentPlanV3.updateClientSiblingLoading ||
      state.treatmentPlanV3.deleteClientSiblingLoading,
  );

  const create = useCallback(
    async (data: ClientSiblingPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        treatmentPlanV3Actions.createTreatmentPlanV3ClientSibling({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.createTreatmentPlanV3ClientSibling.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (siblingId: number, data: ClientSiblingPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.updateTreatmentPlanV3ClientSibling({
          treatmentPlanId,
          siblingId,
          data,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.updateTreatmentPlanV3ClientSibling.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (siblingId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.deleteTreatmentPlanV3ClientSibling({
          treatmentPlanId,
          siblingId,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.deleteTreatmentPlanV3ClientSibling.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return { siblings, isSaving, create, update, remove };
}
